import { useMemo } from 'react';
import classNames from './ChangeLevelPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import {
  BulkOperationResponseData,
  ChangeStudentsLevelBulkOperationItem,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import Button from '@/components/school-management/Button/Button';
import StudentsTable, {
  convertStudentReportItemToStudentsTableData,
} from './StudentsTablePartialSuccesses';
import Download from './Download';

interface PartialSuccessesProps {
  students: StudentReportItem[];
  newLevelLabel: string;
  bulkOperation?: BulkOperationResponseData;
  onClose: () => void;
}

const PartialSuccesses = ({
  students,
  newLevelLabel,
  bulkOperation,
  onClose,
}: PartialSuccessesProps) => {
  const { t } = useAdminTranslation();

  const { studentsTableData, completedCount, totalCount } = useMemo(() => {
    const studentsToProcess = students || [];

    const bulkStudentsMapper = (
      (bulkOperation?.results || []) as ChangeStudentsLevelBulkOperationItem[]
    ).reduce(
      (acc, s) => {
        if (s.studentId) {
          acc[s.studentId] = s;
        }

        return acc;
      },
      {} as {
        [studentId: string]: ChangeStudentsLevelBulkOperationItem;
      }
    );

    const tableData = studentsToProcess.map(s => {
      const isSuccess =
        s.userId in bulkStudentsMapper ? bulkStudentsMapper[s.userId].success : true;
      return convertStudentReportItemToStudentsTableData(s, isSuccess, t);
    });

    const failedCount = tableData.filter(s => !s.isSuccess).length;

    return {
      studentsTableData: tableData,
      completedCount: studentsToProcess.length - failedCount,
      totalCount: studentsToProcess.length,
    };
  }, [students, bulkOperation, t]);

  return (
    <>
      <div>
        <div className={classNames.title} style={{ fontSize: '26px' }}>
          {t('bulkActions.common.partialSuccessTitle')}
        </div>
        <div className={classNames.description}>
          {t('bulkActions.changeLevel.partialSuccesses.description', {
            completed: completedCount,
            total: totalCount,
            newLevel: newLevelLabel,
          })}
        </div>
      </div>
      <StudentsTable students={studentsTableData} />
      <Download
        students={students}
        newLevelLabel={newLevelLabel}
        bulkOperation={bulkOperation}
        bulkOperationState={BulkOperationState.PartialSuccess}
      />
      <div className={classNames.footer}>
        <Button className={classNames.button} onClick={onClose}>
          {t('done')}
        </Button>
      </div>
    </>
  );
};

export default PartialSuccesses;
